import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

export default function DocumentShareDialog({ open, onOpenChange, documentIds = [], onShared }) {
  const [staff, setStaff] = useState([]);
  const [picked, setPicked] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setPicked([]); setResult(null);
      api.get("/users").then((r) => setStaff(r.data.items.filter((u) => u.active !== false)));
    }
  }, [open]);

  const toggle = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const share = async () => {
    if (!picked.length) { toast.error("Select at least one staff member"); return; }
    setSharing(true);
    try {
      const { data } = await api.post("/library/share", { document_ids: documentIds, user_ids: picked });
      setResult(data);
      toast.success(`Shared with ${data.recipients.length} staff member${data.recipients.length > 1 ? "s" : ""}`);
      onShared && onShared();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setSharing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="doc-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Share Documents</DialogTitle>
          <DialogDescription>{documentIds.length} document{documentIds.length > 1 ? "s" : ""} → select staff recipients.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="py-2" data-testid="doc-share-result">
            <div className="flex items-center gap-2 text-[#5B880C] font-semibold mb-3"><CheckCircle2 className="h-5 w-5" />Shared successfully</div>
            <p className="text-sm text-slate-600"><b>{result.documents.length}</b> document(s) shared with:</p>
            <ul className="mt-1 text-sm text-slate-700 list-disc ml-5">{result.recipients.map((r) => <li key={r.id}>{r.name}</li>)}</ul>
            <p className="text-xs text-slate-400 mt-3">By {result.shared_by} · {new Date(result.shared_at).toLocaleString()}</p>
          </div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-slate-100 py-1">
            {staff.map((u) => (
              <label key={u.id} className="flex items-center gap-3 py-2.5 cursor-pointer" data-testid={`share-staff-${u.id}`}>
                <Checkbox checked={picked.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
                <div className="min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{u.name}</p><p className="text-xs text-slate-400 truncate">{u.email} · {u.role?.replace("_", " ")}</p></div>
              </label>
            ))}
          </div>
        )}

        <DialogFooter>
          {result ? <Button onClick={() => onOpenChange(false)} data-testid="doc-share-done">Done</Button> : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={share} disabled={sharing} data-testid="doc-share-confirm">{sharing ? "Sharing…" : `Share with ${picked.length || ""}`.trim()}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
